import { useEffect, useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArchive, IconArchiveOff, IconEdit, IconPlus } from '@tabler/icons-react';

/**
 * Универсальная страница справочника:
 * - заголовок и кнопка «Добавить»;
 * - переключатель «Показывать архивные»;
 * - таблица записей с действиями (правка, архивация/восстановление);
 * - модальное окно создания/правки, рендерится через `renderForm`.
 *
 * Параметризуется тремя generic-типами: Item (DTO записи), Create (DTO создания),
 * Patch (DTO правки). Дублирующая логика страниц сводится к ~30 строкам каждой.
 */

interface BookApi<Item, Create, Patch> {
  list(includeArchived?: boolean): Promise<Item[]>;
  create(dto: Create): Promise<Item>;
  update(id: string, patch: Patch): Promise<Item>;
  archive(id: string): Promise<Item>;
  restore(id: string): Promise<Item>;
}

interface Column<Item> {
  header: string;
  render(item: Item): ReactNode;
}

interface Props<Item extends { id: string; archivedAt: string | null }, Create, Patch> {
  title: string;
  api: BookApi<Item, Create, Patch>;
  columns: Column<Item>[];
  /** Рендерер формы. Принимает «текущее» значение (Item | null) и колбэк onSubmit. */
  renderForm: (args: {
    initial: Item | null;
    onSubmit: (dto: Create | Patch) => Promise<void>;
    submitting: boolean;
  }) => ReactNode;
}

export function ReferenceBookTable<
  Item extends { id: string; archivedAt: string | null },
  Create,
  Patch,
>({ title, api, columns, renderForm }: Props<Item, Create, Patch>) {
  const [items, setItems] = useState<Item[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      setError(null);
      const list = await api.list(showArchived);
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить список');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const closeModal = () => {
    setEditing(null);
    setCreating(false);
  };

  const onSubmit = async (dto: Create | Patch) => {
    setSubmitting(true);
    try {
      if (editing) {
        await api.update(editing.id, dto as Patch);
      } else {
        await api.create(dto as Create);
      }
      await reload();
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSubmitting(false);
    }
  };

  const archiveOrRestore = async (item: Item) => {
    try {
      if (item.archivedAt) await api.restore(item.id);
      else await api.archive(item.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Действие не выполнено');
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{title}</Title>
        <Group>
          <Switch
            label="С архивными"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.currentTarget.checked)}
          />
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            Добавить
          </Button>
        </Group>
      </Group>

      {error && <Text c="red">{error}</Text>}

      <Table striped withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            {columns.map((c) => (
              <Table.Th key={c.header}>{c.header}</Table.Th>
            ))}
            <Table.Th style={{ width: 100 }}>Статус</Table.Th>
            <Table.Th style={{ width: 90 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item) => (
            <Table.Tr key={item.id}>
              {columns.map((c) => (
                <Table.Td key={c.header}>{c.render(item)}</Table.Td>
              ))}
              <Table.Td>
                {item.archivedAt ? (
                  <Badge color="gray">архив</Badge>
                ) : (
                  <Badge color="green">активна</Badge>
                )}
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label="Редактировать">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => setEditing(item)}
                      disabled={!!item.archivedAt}
                    >
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={item.archivedAt ? 'Восстановить' : 'В архив'}>
                    <ActionIcon
                      variant="subtle"
                      color={item.archivedAt ? 'blue' : 'red'}
                      onClick={() => archiveOrRestore(item)}
                    >
                      {item.archivedAt ? (
                        <IconArchiveOff size={16} />
                      ) : (
                        <IconArchive size={16} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={columns.length + 2}>
                <Text c="dimmed" ta="center" py="md">
                  Пока нет записей
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Modal
        opened={creating || !!editing}
        onClose={closeModal}
        title={editing ? 'Редактирование' : 'Новая запись'}
        size="lg"
      >
        {/*
         * Force-remount формы при каждом открытии: иначе useState внутри
         * рендер-функции запоминается с initial=null от первого монтирования
         * и не подхватывает новые данные. Key зависит от id или режима «новая».
         */}
        <div key={editing?.id ?? 'new'}>
          {renderForm({ initial: editing, onSubmit, submitting })}
        </div>
      </Modal>
    </Stack>
  );
}
